import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  DatePicker,
  message,
  Popconfirm,
  Card,
  Space,
  Empty,
  Spin,
  Statistic,
  Tooltip,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPaidOvertimeByUser,
  createPaidOvertime,
  updatePaidOvertime,
  deletePaidOvertime,
  clearPaidOvertimeState,
} from "../redux/slieces/paidOvertimeSlice";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const PaidOvertimeManagement = ({ userId, orgId, adminEmail }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { paidOvertimeEntries, loading } = useSelector(
    (state) => state.paidOvertime
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (userId) {
      dispatch(clearPaidOvertimeState());
      dispatch(fetchPaidOvertimeByUser(userId));
    }
    return () => dispatch(clearPaidOvertimeState());
  }, [dispatch, userId]);

  // Client-side date range filtering
  const filteredEntries = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return paidOvertimeEntries;
    }
    const startUnix = dateRange[0].startOf("day").unix();
    const endUnix = dateRange[1].endOf("day").unix();
    return paidOvertimeEntries.filter((entry) => {
      const entryUnix = dayjs(entry.date).unix();
      return entryUnix >= startUnix && entryUnix <= endUnix;
    });
  }, [paidOvertimeEntries, dateRange]);

  // Summary statistics based on filtered data
  const totalMinutes = filteredEntries.reduce(
    (sum, entry) => sum + (entry.hours || 0) * 60 + (entry.minutes || 0),
    0
  );
  const totalH = Math.floor(totalMinutes / 60);
  const totalM = totalMinutes % 60;

  const handleAdd = () => {
    setEditingEntry(null);
    form.resetFields();
    form.setFieldsValue({ date: dayjs() });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingEntry(record);
    form.setFieldsValue({
      date: dayjs(record.date),
      hours: record.hours,
      minutes: record.minutes,
      comment: record.comment,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deletePaidOvertime(id)).unwrap();
      message.success(t("paidOvertime.deleteSuccess"));
    } catch (error) {
      message.error(t("paidOvertime.deleteError") + ": " + (error?.message || error));
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        userId: userId,
        organizationId: orgId,
        date: values.date.toISOString(),
        hours: values.hours || 0,
        minutes: values.minutes || 0,
        comment: values.comment || "",
        createdBy: adminEmail,
        updatedBy: adminEmail,
      };

      if (editingEntry) {
        const updatePayload = {
          ...payload,
          id: editingEntry.id,
        };
        await dispatch(
          updatePaidOvertime({ id: editingEntry.id, data: updatePayload })
        ).unwrap();
        message.success(t("paidOvertime.updateSuccess"));
        dispatch(fetchPaidOvertimeByUser(userId));
      } else {
        await dispatch(createPaidOvertime(payload)).unwrap();
        message.success(t("paidOvertime.createSuccess"));
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingEntry(null);
    } catch (error) {
      if (error.errorFields) return;
      message.error(t("paidOvertime.saveError") + ": " + (error?.message || error));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingEntry(null);
  };

  const columns = [
    {
      title: t("paidOvertime.date"),
      dataIndex: "date",
      key: "date",
      render: (text) => dayjs(text).format("DD.MM.YYYY"),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: "descend",
    },
    {
      title: t("paidOvertime.duration"),
      key: "duration",
      sorter: (a, b) =>
        (a.hours * 60 + a.minutes) - (b.hours * 60 + b.minutes),
      render: (_, record) => `${record.hours}h ${record.minutes}m`,
    },
    {
      title: t("paidOvertime.comment"),
      dataIndex: "comment",
      key: "comment",
      ellipsis: { showTitle: false },
      render: (text) => (
        <Tooltip title={text}>{text || "—"}</Tooltip>
      ),
    },
    {
      title: t("action"),
      key: "action",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            data-testid="edit-overtime-button"
          />
          <Popconfirm
            title={t("paidOvertime.confirmDelete")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("paidOvertime.yes")}
            cancelText={t("cancel")}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              data-testid="delete-overtime-button"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="paid-overtime-management" style={{ padding: "8px 0" }}>
      {/* Summary statistics card */}
      <Card size="small" style={{ marginBottom: 16 }} data-testid="overtime-summary">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Row gutter={32}>
              <Col>
                <Statistic
                  title={t("paidOvertime.totalOvertime")}
                  value={`${totalH}h ${totalM}m`}
                  prefix={<ClockCircleOutlined />}
                />
              </Col>
              <Col>
                <Statistic
                  title={t("paidOvertime.totalEntries")}
                  value={filteredEntries.length}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Action bar: Add button + Date range filter */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            data-testid="add-overtime-button"
          >
            {t("paidOvertime.addButton")}
          </Button>
        </Col>
        <Col>
          <Space>
            <FilterOutlined />
            <RangePicker
              format="DD.MM.YYYY"
              onChange={(dates) => setDateRange(dates)}
              value={dateRange}
              placeholder={[t("paidOvertime.startDate"), t("paidOvertime.endDate")]}
            />
            {dateRange && (
              <Button
                icon={<ClearOutlined />}
                size="small"
                onClick={() => setDateRange(null)}
              >
                {t("paidOvertime.clearFilter")}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filteredEntries.length === 0 ? (
        <Empty description={dateRange ? t("paidOvertime.noFilteredEntries") : t("paidOvertime.noEntries")} />
      ) : (
        <Table
          dataSource={filteredEntries}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t("of")} ${total} ${t("paidOvertime.entries")}`,
          }}
          size="small"
        />
      )}

      <Modal
        title={
          editingEntry
            ? t("paidOvertime.editTitle")
            : t("paidOvertime.addTitle")
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingEntry ? t("paidOvertime.save") : t("paidOvertime.add")}
        cancelText={t("cancel")}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="date"
            label={t("paidOvertime.date")}
            rules={[
              {
                required: true,
                message: t("paidOvertime.dateRequired"),
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
              placeholder={t("paidOvertime.selectDate")}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hours"
                label={t("paidOvertime.hours")}
                rules={[
                  {
                    required: true,
                    message: t("paidOvertime.hoursRequired"),
                  },
                  {
                    type: "number",
                    min: 0,
                    message: t("paidOvertime.hoursMin"),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="minutes"
                label={t("paidOvertime.minutes")}
                rules={[
                  {
                    type: "number",
                    min: 0,
                    max: 59,
                    message: t("paidOvertime.minutesRange"),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={59}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="comment"
            label={t("paidOvertime.comment")}
          >
            <TextArea
              rows={3}
              placeholder={t("paidOvertime.commentPlaceholder")}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaidOvertimeManagement;
